import { useState, useMemo } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { Typography, Box, Button, CircularProgress } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { IRegistrant } from "../../types/registrant";
import BasicTabs from "../common/BasicTabs";

import { Container, FlexBetween } from "../../commonStyles";
import StandardTable from "../tables/StandardTable";
import { capitalizeFirstLetter, convertAllDates, dashToTitle } from "../../utils/function";
import { useSelector } from "react-redux";
import { selectCalls } from "../../features/registrants/registrantsSlice";
import { useAppDispatch } from "../../app/hooks";
import { showErrorSnackbar, showSuccessSnackbar } from "../../features/snackbar/snackbarSlice";
import { selectUser } from "../../features/auth/authSlice";
import { usePhoneQuery } from "../../features/registrants/registrantHooks";
import Conversations from "./Conversations";
import CallOutcomeDialog from "./CallOutcomeDialog";

const Messages = ({ registrant }: ChildProps) => {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const calls = useSelector(selectCalls);
  const [value, setValue] = useState(0);
  const [callOpen, setCallOpen] = useState<boolean>(false);

  usePhoneQuery(registrant && registrant.primaryPhone ? registrant._id : "", registrant.project._id, user?.type!, "all");

  const [downloadAudio] = useLazyQuery(DOWNLOADAUDIO, {
    onCompleted: (data) => {
      const element = document.createElement("a");
      element.href = data.downloadAudio;
      element.target = "_blank";
      element.click();
    },
    onError: (e) => {
      console.log(e);
    },
  });

  const [callRegistrant, { loading }] = useLazyQuery(CALLREGISTRANT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(data.callRegistrant));
    },
    onError: (e) => {
      // The call never started, so there is nothing to log in the call modal
      setCallOpen(false);
      storeDispatch(showErrorSnackbar(e.message || "Error calling registrant"));
    },
  });

  const handleCall = (project: boolean) => {
    if (registrant.emailUnsubscribed || registrant.textUnsubscribed) return storeDispatch(showErrorSnackbar("Registrant is unsubscribed"));

    let phone = (registrant.primaryPhone || "").replace(/\D/g, "");
    // The API adds +1, so drop a leading country code
    if (phone.length === 11 && phone.startsWith("1")) phone = phone.slice(1);
    if (phone.length !== 10) {
      return storeDispatch(showErrorSnackbar("Only US/Canadian Phone Numbers are allowed"));
    }

    setCallOpen(true);
    if (project) {
      callRegistrant({ variables: { project: registrant.project._id, customerPhone: parseInt(phone, 10), callType: "project", registrant: registrant._id } });
    } else {
      callRegistrant({ variables: { project: registrant.project._id, customerPhone: parseInt(phone, 10), callType: "user", registrant: registrant._id } });
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "Date",
        accessor: (rowData: any) => convertAllDates(rowData.dateCreated, "PPpp"),
      },
      {
        Header: "RDS User",
        accessor: (rowData: any) => rowData.user,
      },
      {
        Header: "Phone Number",
        accessor: (rowData: any) => rowData.phone,
      },
      {
        Header: "Status",
        accessor: (rowData: any) => dashToTitle(rowData.status),
      },
      {
        Header: "Direction",
        accessor: (rowData: any) => capitalizeFirstLetter(rowData.direction),
      },
      {
        Header: "Duration(s)",
        accessor: (rowData: any) => (rowData.duration ? rowData.duration : rowData.recording_duration),
      },
    ];
  }, [downloadAudio]);

  const tabs = [
    {
      label: "Calls",
      component: (
        <Box sx={{ mt: 2 }}>
          <StandardTable data={calls} columns={columns} />
        </Box>
      ),
    },
    {
      label: "Conversations",
      component: <Conversations registrant={registrant} />,
    },
  ];

  return (
    <Container sx={{ height: "100%" }}>
      <FlexBetween sx={{ mb: 2 }}>
        <Typography variant="h2" component="div" gutterBottom>
          <strong>Messages/Phone Calls</strong>
        </Typography>
        {loading || registrant.emailUnsubscribed || registrant.textUnsubscribed ? null : (
          <Box>
            {user?.trackingPhone ? (
              <Button onClick={() => handleCall(false)} color="primary" variant="contained" endIcon={<PhoneIcon />}>
                Call using Personal Sales Number
              </Button>
            ) : null}
            {registrant.project?.trackingPhone ? (
              <Button onClick={() => handleCall(true)} color="primary" variant="contained" endIcon={<PhoneIcon />} sx={{ ml: 1 }}>
                Call using Project Number
              </Button>
            ) : null}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <Box>
          <Box sx={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
            <CircularProgress sx={{ m: "0 auto" }} />
            <Box sx={{ mt: 2 }}>Calling Registrant...</Box>
          </Box>
        </Box>
      ) : (
        <BasicTabs color="#00142a" tabs={tabs} sub={false} value={value} setValue={setValue} />
      )}
      <CallOutcomeDialog open={callOpen} registrant={registrant} onClose={() => setCallOpen(false)} />
    </Container>
  );
};

interface ChildProps {
  registrant: IRegistrant;
}

const DOWNLOADAUDIO = gql`
  query downloadAudio($link: String!) {
    downloadAudio(link: $link)
  }
`;

const CALLREGISTRANT = gql`
  query callRegistrant($project: MongoID, $customerPhone: Float!, $callType: String!, $registrant: MongoID) {
    callRegistrant(project: $project, customerPhone: $customerPhone, callType: $callType, registrant: $registrant)
  }
`;

export default Messages;
